import { createSelector } from "@reduxjs/toolkit";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { getBlogById as onGetBlogById } from "slices/thunk";
import { Section } from "../AddBlogForm/createBlog";
import PreviewParagraph from "../Components/PreviewBlog/Section/Paragraph/paragraph";
import PreviewBlogInfo from "../Components/PreviewBlog/PreviewBlogInfo/blogInfo";
import PreviewImage from "../Components/PreviewBlog/Section/Image/image";
import PreviewList from "../Components/PreviewBlog/Section/List/list";
import PreviewQuote from "../Components/PreviewBlog/Section/Quote/quote";
import PreviewVideo from "../Components/PreviewBlog/Section/Video/video";
import PreviewTable from "../Components/PreviewBlog/Table/table";
import PreviewEmbed from "../Components/PreviewBlog/Section/Embed/embed";
import PreviewCode from "../Components/PreviewBlog/Section/Code/code";
import BreadCrumb from "Common/BreadCrumb";
import PreviewProduct from "../Components/PreviewBlog/Section/Product/product";

const PreviewBlog = () => {
  const { id } = useParams();
  const dispatch = useDispatch<any>();
  const location = useLocation(); // Get the location object
  const tagsRef = useRef<HTMLDivElement | null>(null); // Create a ref for the tags section

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      blog: state.blog,
      isLoading: state.isLoading,
    })
  );
  const { blog } = useSelector(selectDataList);

  useEffect(() => {
    if (id) {
      dispatch(onGetBlogById(id)); // Fetch data from Redux
    }
  }, [id, dispatch]);

  useEffect(() => {
    // Check if the URL has the scrollTo parameter
    const params = new URLSearchParams(location.search);
    if (params.get("scrollTo") === "tags" && tagsRef.current) {
      tagsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.search, tagsRef]);

  return (
    <div className="">
      {/* {blog?.title} */}
      <BreadCrumb title={blog.title} pageTitle="Blog" />
      <div className="bg-white card p-5">
        <PreviewBlogInfo blog={blog} />
        {blog.sections?.map((section: Section) => {
          return (
            <div>
              <p className="text-xl font-medium pt-5">{section.title}</p>
              {section.body.map((subSection) => {
                return (
                  <>
                    <PreviewParagraph subSection={subSection} />
                    <PreviewProduct subSection={subSection} />
                    <PreviewImage subSection={subSection} />
                    <PreviewList subSection={subSection} />
                    <PreviewQuote subSection={subSection} />
                    <PreviewVideo subSection={subSection} />
                    <PreviewTable subSection={subSection} />
                    <PreviewEmbed subSection={subSection} />
                    <PreviewCode subSection={subSection} />
                  </>
                );
              })}
            </div>
          );
        })}
        <div className="flex pt-5 gap-x-5">
          {blog.categories?.map((category: string) => {
            return (
              <span className="px-2.5 py-0.5 text-xs inline-block font-semibold rounded-full border bg-white border-slate-400 text-slate-500 dark:bg-zink-700 dark:border-zink-400 dark:text-zink-200">
                {category}
              </span>
            );
          })}
        </div>
        <div className="">
          {blog.tags && (
            <div className="pt-5 flex gap-x-2" ref={tagsRef}>
              Tags:
              {blog.tags?.map((tag: string, index: number) => (
                <span
                  key={index}
                  className="px-2.5 py-0.5 text-xs inline-block font-semibold rounded-full border bg-white border-slate-400 text-slate-500 dark:bg-zink-700 dark:border-zink-400 dark:text-zink-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewBlog;
